import React from 'react';

function TermsOfUse() {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200 shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold">
                <span className="text-red-600">Terms</span>
                <span className="text-blue-900"> of Use</span>
              </h1>
              <p className="text-sm text-gray-600 mt-1">Freedom Contracting Group</p>
            </div>
            <a
              href="/"
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Back to Home"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </a>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
          
          {/* Last Updated */}
          <div className="mb-8 pb-6 border-b border-gray-200">
            <p className="text-sm text-gray-500">Last Updated: January 20, 2026</p>
          </div>

          {/* Agreement */}
          <section className="mb-10">
            <div className="bg-blue-50 border-l-4 border-blue-900 rounded-r-lg p-6">
              <p className="text-gray-700 leading-relaxed">
                By accessing or using freedomcg.net (the "Website"), you agree to these Terms of Use. If you do not agree, please do not use the Website.
              </p>
            </div>
          </section>

          {/* About the Website */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-blue-900 mb-4">About This Website</h2>
            <p className="text-gray-700 leading-relaxed">
              This Website is owned and operated by Freedom Contracting Group ("FCG," "we," "us," or "our"), a commercial construction company based in Chicago, Illinois. The Website provides information about our services and allows visitors to contact us.
            </p>
          </section>

          {/* Eligibility */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-blue-900 mb-4">Eligibility</h2>
            <p className="text-gray-700 leading-relaxed">
              You must be at least 18 years old to use this Website. By using the Website, you represent that you meet this requirement.
            </p>
          </section>

          {/* Permitted Use */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-blue-900 mb-4">Permitted Use</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              You may use this Website to:
            </p>
            <div className="bg-green-50 border-l-4 border-green-600 rounded-r-lg p-6">
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span>Learn about Freedom Contracting Group and our services</span>
                </li>
                <li className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span>Contact us through our online form</span>
                </li>
                <li className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span>View publicly available content</span>
                </li>
              </ul>
            </div>
          </section>

          {/* Prohibited Uses */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-blue-900 mb-4">Prohibited Uses</h2>
            <p className="text-gray-700 leading-relaxed mb-4">You may not:</p>
            <div className="bg-red-50 border-l-4 border-red-600 rounded-r-lg p-6">
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                  <span>Use the Website for any unlawful purpose</span>
                </li>
                <li className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                  <span>Copy, reproduce, or distribute our content without permission</span>
                </li>
                <li className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                  <span>Attempt to gain unauthorized access to our systems</span>
                </li>
                <li className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                  <span>Use automated tools to scrape or collect data from the Website</span>
                </li>
                <li className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                  <span>Introduce viruses, malware, or other harmful code</span>
                </li>
                <li className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                  <span>Impersonate another person or entity</span>
                </li>
              </ul>
            </div>
          </section>

          {/* Intellectual Property */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-blue-900 mb-4">Intellectual Property</h2>
            <p className="text-gray-700 leading-relaxed">
              All content on this Website—including text, images, graphics, logos, and designs—is owned by or licensed to Freedom Contracting Group and is protected by intellectual property laws. You may not use our content without written permission.
            </p>
          </section>

          {/* Informational Purpose */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-blue-900 mb-4">Informational Purpose Only</h2>
            <p className="text-gray-700 leading-relaxed">
              The information on this Website is for general informational purposes only. It does not constitute a contract or guarantee of services. Any construction services would be subject to a separate written agreement.
            </p>
          </section>

          {/* Disclaimer */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-blue-900 mb-4">Disclaimer</h2>
            <div className="bg-slate-50 rounded-lg p-6">
              <p className="text-gray-700 leading-relaxed">
                <strong>THE WEBSITE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND.</strong> We make no guarantees about the accuracy, completeness, or availability of the Website. We are not responsible for any errors or omissions in the content.
              </p>
            </div>
          </section>

          {/* Limitation of Liability */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-blue-900 mb-4">Limitation of Liability</h2>
            <p className="text-gray-700 leading-relaxed">
              To the maximum extent permitted by law, Freedom Contracting Group shall not be liable for any indirect, incidental, or consequential damages arising from your use of the Website. Your sole remedy for dissatisfaction is to stop using the Website.
            </p>
          </section>

          {/* Third-Party Links */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-blue-900 mb-4">Third-Party Links</h2>
            <p className="text-gray-700 leading-relaxed">
              The Website may contain links to third-party websites. We are not responsible for the content or practices of those sites.
            </p>
          </section>

          {/* Governing Law */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-blue-900 mb-4">Governing Law</h2>
            <p className="text-gray-700 leading-relaxed">
              These Terms are governed by the laws of the State of Illinois. Any disputes shall be resolved in the state or federal courts located in Illinois.
            </p>
          </section>

          {/* Changes */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-blue-900 mb-4">Changes to These Terms</h2>
            <p className="text-gray-700 leading-relaxed">
              We may update these Terms from time to time. Changes will be posted on this page with an updated "Last Updated" date. Continued use of the Website after changes constitutes acceptance of the new Terms.
            </p>
          </section>

          {/* Contact */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-blue-900 mb-4">Contact Us</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you have questions about these Terms, please contact us:
            </p>
            <div className="bg-slate-50 rounded-lg p-6">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-blue-900 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                  </svg>
                  <div>
                    <p className="font-medium text-blue-900">Email:</p>
                    <a href="mailto:hello@freedomcg.net" className="text-red-600 hover:underline">
                      hello@freedomcg.net
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-blue-900 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                  </svg>
                  <div>
                    <p className="font-medium text-blue-900">Mail:</p>
                    <p className="text-gray-700">Freedom Contracting Group</p>
                    <p className="text-gray-700">111 W Jackson Blvd, Suite 1700</p>
                    <p className="text-gray-700">Chicago, IL 60604</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Back to Top Button */}
          <div className="mt-12 pt-8 border-t border-gray-200 text-center">
            <button
              onClick={scrollToTop}
              className="inline-flex items-center gap-2 bg-blue-900 text-white hover:bg-red-600 font-medium py-3 px-6 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 10l7-7m0 0l7 7m-7-7v18"></path>
              </svg>
              Back to Top
            </button>
          </div>
        </div>

        {/* Contact CTA */}
        <div className="mt-12 bg-gradient-to-r from-blue-900 to-red-900 rounded-2xl p-8 md:p-12 text-white text-center">
          <h2 className="text-3xl font-bold mb-4">Have Questions?</h2>
          <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
            We're here to help with any questions about using our website.
          </p>
          <a 
            href="mailto:hello@freedomcg.net"
            className="inline-flex items-center gap-2 bg-white text-blue-900 hover:bg-gray-100 font-semibold py-4 px-8 rounded-xl transition-all shadow-lg"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
            </svg>
            Contact Us
          </a>
        </div>
      </div>
    </div>
  );
}

export default TermsOfUse;